import { useEffect, useState } from 'react';

import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Button, FieldLabel, Password, showSnackbar, TextInput } from 'fluxo-ui';

import { AuthShell } from '../components/common';
import { ROUTES } from '../common/constants';
import { useAuthStore } from '../store/authStore';

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { login, isAuthenticated, error, clearError } = useAuthStore();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
    const [isLoading, setIsLoading] = useState(false);
    const sessionExpired = searchParams.get('sessionExpired') === '1';

    useEffect(() => {
        if (isAuthenticated) {
            navigate(ROUTES.DASHBOARD);
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (sessionExpired) {
            showSnackbar('Your session has expired. Please log in again.', 'Session Expired', { type: 'warning' });
        }
    }, [sessionExpired]);

    useEffect(() => {
        if (error) {
            clearError();
        }
    }, [error, clearError]);

    const validate = () => {
        const newErrors: { email?: string; password?: string } = {};

        if (!email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!password) {
            newErrors.password = 'Password is required';
        } else if (password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        setIsLoading(true);
        try {
            await login({ email: email.trim(), password });
            navigate(ROUTES.DASHBOARD);
        } catch (err: any) {
            const message = err?.message || 'Invalid email or password';
            showSnackbar(message, 'Login Failed', { type: 'error', timeout: 5000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <AuthShell>
            <div className="text-center mb-8">
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white mb-2">Welcome back</h1>
                <p className="text-sm text-slate-500 dark:text-slate-400">Log in to your Testam account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <FieldLabel label="Email" required error={errors.email}>
                    <TextInput
                        value={email}
                        onChange={(e) => {
                            setEmail(e.value);
                            if (errors.email) setErrors({ ...errors, email: undefined });
                        }}
                        placeholder="Enter your email"
                        autoComplete="email"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <FieldLabel label="Password" required error={errors.password}>
                    <Password
                        value={password}
                        onChange={(e) => {
                            setPassword(e.value);
                            if (errors.password) setErrors({ ...errors, password: undefined });
                        }}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                        disabled={isLoading}
                    />
                </FieldLabel>

                <div className="flex items-center justify-between">
                    <Link
                        to={ROUTES.FORGOT_PASSWORD}
                        className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300"
                    >
                        Forgot password?
                    </Link>
                </div>

                <Button type="submit" label="Log In" variant="primary" className="w-full" isLoading={isLoading} disabled={isLoading} />
            </form>

            <div className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Don't have an account?{' '}
                <Link
                    to={ROUTES.SIGNUP}
                    className="text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium transition-colors"
                >
                    Sign up
                </Link>
            </div>
        </AuthShell>
    );
};

export default LoginPage;
